import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tag, Loader2, X, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Kortingscode-vak op /prijzen. Bij succes geeft `onApplied` de validate-response
// terug ({code, percent, applies_to, prices}); `onClear` haalt de korting weg.
// `?code=KORTING-XXXXXX` in de URL (uit de kortingsmail) wordt direct toegepast.
export default function DiscountCodeBox({ discount, onApplied, onClear }) {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const validate = useCallback(async (raw) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mollie/discount/validate", { code: raw.trim() });
      onApplied(data);
      toast.success(`Korting van ${data.percent}% toegepast`);
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Code ongeldig.");
    }
    setBusy(false);
  }, [onApplied]);

  useEffect(() => {
    const fromUrl = params.get("code");
    if (!fromUrl) return;
    setParams({}, { replace: true });
    validate(fromUrl.toUpperCase());
  }, [params, setParams, validate]);

  const apply = (e) => {
    e.preventDefault();
    if (code.trim()) validate(code);
  };

  if (discount) {
    return (
      <div data-testid="discount-applied" className="border-2 border-positive bg-positive/10 p-4 flex items-center gap-3 flex-wrap">
        <Check className="w-5 h-5 text-positive shrink-0" />
        <div className="flex-1 text-sm">
          <b>Kortingscode {discount.code}</b> — {discount.percent}% korting,{" "}
          {discount.applies_to === "recurring" ? "ook op elke jaarlijkse verlenging" : "alleen op het eerste jaar"}.
        </div>
        <button type="button" onClick={onClear} data-testid="discount-clear-btn"
          className="text-xs underline inline-flex items-center gap-1 hover:text-destructive">
          <X className="w-3.5 h-3.5" /> Verwijderen
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={apply} data-testid="discount-form" className="border-2 border-foreground bg-card p-4 flex items-end gap-3 flex-wrap">
      <div className="flex-1 min-w-[220px]">
        <label className="stat-label inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Kortingscode</label>
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="KORTING-XXXXXX" data-testid="discount-code-input"
          className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num tracking-widest focus:outline-none focus:ring-2 focus:ring-primary" />
      </div>
      <button type="submit" disabled={busy || !code.trim()} data-testid="discount-apply-btn"
        className="btn-brutal bg-foreground text-background px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Toepassen
      </button>
    </form>
  );
}
